/**
 * meetingConstants.ts
 * Named constants for the NetworkClub Meeting System.
 * Rule 9: No hardcoded strings, colors, or timeouts anywhere in meeting components.
 */

// ── Firestore collection names ─────────────────────────────────────────────
export const COLLECTIONS = {
  LIVE_PITCHES: 'meetingLivePitches',
  ATTENDANCE: 'meetingAttendance',
  SETTINGS: 'meetingSettings',
  CONNECTIONS: 'meetingConnections',
  RECORDINGS: 'meetingRecordings',
  REPORTS: 'meetingReports',
  GEO_REPUTATION: 'geoReputationContent',
  PRESENTATION_TIMELINES: 'meetingPresentationTimelines',
  INBOX: 'inboxItems',
} as const;

// ── Storage paths ──────────────────────────────────────────────────────────
export const STORAGE_PATHS = {
  pitchMedia: (entityId: string, userId: string) =>
    `pitchMedia/${entityId}/${userId}`,
  meetingReport: (entityId: string, roomName: string) =>
    `meetingReports/${entityId}/${roomName}.pdf`,
  countdownMusic: (entityId: string) =>
    `meetingMedia/${entityId}/music`,
  countdownVideo: (entityId: string) =>
    `meetingMedia/${entityId}/video`,
  transitionMusic: (entityId: string) =>
    `meetingMedia/${entityId}/transitionMusic`,
  templatePdf: (entityId: string) =>
    `meetingMedia/${entityId}/template.pdf`,
} as const;

// ── Timer defaults ─────────────────────────────────────────────────────────
export const TIMER_DEFAULT_SECONDS = 60;
export const COUNTDOWN_DEFAULT_MINUTES = 4;
export const MEDIA_DISPLAY_DEFAULT_SECONDS = 5;
export const CAROUSEL_TICK_MS = 500;
export const SELF_START_WARNING_SECONDS = 10; // overlay appears X sec before end

// ── Upload limits ──────────────────────────────────────────────────────────
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;   // 100 MB
export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;     // 8 MB
/** Beamer/Canva-template PDF (één bestand naar Storage) */
export const MAX_TEMPLATE_PDF_BYTES = 100 * 1024 * 1024; // 100 MB
export const PHOTO_SOFT_LIMIT = 10;                 // warn above this count

// ── UI durations ───────────────────────────────────────────────────────────
export const FEEDBACK_TOAST_MS = 3_000;
export const SELF_START_DISMISS_MS = 300;           // animation out

// ── Accepted MIME types ────────────────────────────────────────────────────
export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
export const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// ── Theme defaults (overridden by presentationColors prop) ─────────────────
export const THEME_DEFAULTS = {
  primary: '#2D5E4F',
  secondary: '#1B3A4B',
  accent: '#7A9E8E',   // sage lichtgroen
} as const;

// ── Fase 3 constants ──────────────────────────────────────────────────────
export const QR_LANDING_BASE_PATH = '/meeting';
export const TRANSITION_DEFAULT_SECONDS = 10;   // overgang tussen pitchers
export const PRELOAD_WINDOW = 2;                 // aantal volgende pitchers om voor te laden
export const TRANSITION_MUSIC_FADE_SECONDS = 2; // fade-in/out duur muziek
export const GEO_CONTENT_MAX_WORDS = 80;
export const ATTENDANCE_ALERT_THRESHOLD_PCT = 60;  // < 60% → rode markering
export const REACTIONS_FLOATING_DURATION_MS = 2_500;
/** Zelfde major/minor als `pdfjs-dist` in package.json (anders faalt laden/renderen stil of met fout). */
export const PDF_WORKER_SRC = 'https://unpkg.com/pdfjs-dist@5.5.207/build/pdf.worker.min.mjs';

// ── Fase 3 collection names ────────────────────────────────────────────────
// (Added to existing COLLECTIONS object above — re-export for clarity)
export const FASE3_COLLECTIONS = {
  CONNECTIONS: 'meetingConnections',
  REACTIONS: 'meetingReactions',
  GEO_REPUTATION: 'geoReputationContent',
} as const;

// ── Emoji reactie types ────────────────────────────────────────────────────
export const REACTIONS = [
  { id: 'interesting', emoji: '💡', label: 'Interessant' },
  { id: 'top_pitch',   emoji: '🔥', label: 'Top pitch'  },
  { id: 'connect',     emoji: '🤝', label: 'Wil connecten' },
] as const;

export type ReactionId = typeof REACTIONS[number]['id'];

// ── Dutch UI strings (i18n-ready) ──────────────────────────────────────────
export const STRINGS = {
  reactions: {
    sentLabel: 'Reactie verstuurd!',
    connectSent: 'Connectieverzoek opgeslagen — je ontvangt na de meeting een uitnodiging.',
  },
  geo: {
    pendingReview: 'Nieuwe GEO content klaar voor jouw goedkeuring',
    approved: 'GEO content gepubliceerd op de Expertstatus pagina',
    optOut: 'Dit lid heeft opt-out ingesteld voor GEO content',
  },
  template: {
    uploadLabel: 'Canva PDF uploaden',
    renderError: 'Template kon niet worden geladen.',
    noTemplate: 'Geen template geladen — gebruik huisstijlkleuren',
    pdfTooLarge: (maxMb: number) => `PDF is te groot (max ${maxMb} MB).`,
  },
  attendance: {
    noHistory: 'Nog geen bijeenkomstgeschiedenis',
    belowThreshold: (pct: number) => `⚠ Aanwezig bij slechts ${pct}% van de meetings`,
    exportLabel: 'Exporteren als CSV',
  },
  guest: {
    heading: 'Welkom bij de bijeenkomst!',
    sub: 'Vul je gegevens in om deel te nemen.',
    namePlaceholder: 'Jouw naam',
    companyPlaceholder: 'Bedrijf',
    searchPlaceholder: 'Wat zoek jij? (optioneel)',
    mentionPlaceholder: '@naam van het lid dat jou uitnodigde',
    ctaLabel: 'Deelnemen',
    successHeading: 'Welkom!',
    successSub: 'Je bent toegevoegd aan de bijeenkomst.',
  },
  selfStart: {
    heading: 'Jij bent aan de beurt! 🎤',
    subheading: 'Tik op de knop om je pitch te starten.',
    cta: 'Start mijn pitch',
    loading: 'Even geduld…',
  },
  countdown: {
    heading: (minutes: number) => `Over ${minutes} ${minutes === 1 ? 'minuut' : 'minuten'} starten we`,
    sub: '',
    startButton: 'Start countdown',
    stopButton: 'Stop countdown',
    musicLabel: 'Achtergrondmuziek',
    videoLabel: 'Sfeer-video',
  },
  upload: {
    videoProgress: (pct: number) => `Video uploaden… ${pct}%`,
    videoSuccess: 'Video opgeslagen!',
    videoError: 'Upload mislukt. Probeer opnieuw.',
    videoTooLarge: 'Video is te groot (max 100 MB).',
    videoWrongType: 'Alleen mp4, webm, ogg of mov bestanden.',
    photoTooLarge: 'Foto is te groot (max 8 MB).',
    photoSoftLimit: 'Meer dan 10 foto\'s kan de laadtijd vertragen.',
  },
  rapport: {
    generating: 'Rapport genereren…',
    success: 'Rapport klaar! Klik om te downloaden.',
    error: 'Rapport aanmaken mislukt.',
    downloadButton: 'Rapport downloaden',
    closeButton: 'Meeting sluiten',
  },
  errors: {
    notLoggedIn: 'Je bent niet ingelogd.',
    noEntity: 'Clubgegevens ontbreken.',
    generic: 'Er ging iets mis. Probeer het opnieuw.',
  },
} as const;
